import React, { useState } from "react";
import styled from "styled-components";
import { useQuery } from "react-query";
import { Button, COLOR_SYSTEM, Text, Spinner } from "components/atoms";
import { StepData } from "./index";
import { groundTruthApi } from "services/api/aiReviewTest/groundTruthApi";

interface ExperimentStep5Props {
  data: StepData;
  onNext: (data: Partial<StepData>) => void;
  onBack: () => void;
}

const ExperimentStep5: React.FC<ExperimentStep5Props> = ({ data, onNext, onBack }) => {
  const [selectedIds, setSelectedIds] = useState({
    patientIds: data.patientIds || [],
    aiReviewIds: data.aiReviewIds || [],
    aiReviewEvaluationIds: data.aiReviewEvaluationIds || [],
  });

  // Ground Truth가 있는 환자 목록 조회 (모드 1용)
  const { data: patientsWithGTResponse, isLoading: isLoadingPatients } = useQuery({
    queryKey: ["groundTruths", "patients", data.promptType],
    queryFn: () => groundTruthApi.findPatientsWithGroundTruth({ promptType: data.promptType }),
    enabled: !!data.modes?.createMode && !!data.promptType,
  });

  const patientIds = patientsWithGTResponse?.data?.data?.patientIds || [];
  const patients = patientsWithGTResponse?.data?.data?.patients || [];

  // TODO: 모드 2, 3를 위한 API 연동 추가
  const aiReviewIds: number[] = []; // 임시
  const evaluationIds: number[] = []; // 임시

  const handlePatientToggle = (patientId: number) => {
    setSelectedIds((prev) => ({
      ...prev,
      patientIds: prev.patientIds.includes(patientId)
        ? prev.patientIds.filter((id) => id !== patientId)
        : [...prev.patientIds, patientId],
    }));
  };

  const handleSelectAll = (type: "patient" | "aiReview" | "evaluation") => {
    if (type === "patient") {
      setSelectedIds((prev) => ({
        ...prev,
        patientIds: prev.patientIds.length === patientIds.length ? [] : [...patientIds],
      }));
    }
    // TODO: 다른 타입에 대한 전체 선택 구현
  };

  const validate = () => {
    const { createMode, evaluateMode, reevaluateMode } = data.modes || {};

    if (createMode && selectedIds.patientIds.length === 0) {
      return false;
    }
    if (evaluateMode && selectedIds.aiReviewIds.length === 0) {
      return false;
    }
    if (reevaluateMode && selectedIds.aiReviewEvaluationIds.length === 0) {
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (!validate()) return;

    onNext({
      patientIds: selectedIds.patientIds,
      aiReviewIds: selectedIds.aiReviewIds,
      aiReviewEvaluationIds: selectedIds.aiReviewEvaluationIds,
    });
  };

  return (
    <Container>
      <StepTitle>대상 선택</StepTitle>
      <StepDescription>
        실험을 수행할 대상을 선택하세요. 선택한 모드에 따라 필요한 대상이 표시됩니다.
      </StepDescription>

      {data.modes?.createMode && (
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>환자 선택 (Ground Truth가 있는 환자)</SectionTitle>
            <Button
              size="Small"
              type="Text"
              variant="Ghost"
              color="Primary"
              onClick={() => handleSelectAll("patient")}
            >
              {selectedIds.patientIds.length === patientIds.length ? "전체 해제" : "전체 선택"}
            </Button>
          </SectionHeader>

          {isLoadingPatients ? (
            <LoadingContainer>
              <Spinner size="Medium" />
            </LoadingContainer>
          ) : patientIds.length === 0 ? (
            <EmptyMessage>
              선택한 프롬프트 타입에 대한 Ground Truth가 있는 환자가 없습니다.
            </EmptyMessage>
          ) : (
            <SelectionGrid>
              {patientIds.map((patientId) => {
                const patient = patients.find((p) => p.id === patientId);
                return (
                  <PatientCard
                    key={patientId}
                    $selected={selectedIds.patientIds.includes(patientId)}
                    onClick={() => handlePatientToggle(patientId)}
                  >
                    <PatientInfo patient={patient} patientId={patientId} />
                    <CheckBox $selected={selectedIds.patientIds.includes(patientId)}>
                      {selectedIds.patientIds.includes(patientId) && "✓"}
                    </CheckBox>
                  </PatientCard>
                );
              })}
            </SelectionGrid>
          )}
          <SelectedCount>{selectedIds.patientIds.length}명 선택됨</SelectedCount>
        </SectionContainer>
      )}

      {data.modes?.evaluateMode && (
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>AI Review 선택</SectionTitle>
          </SectionHeader>
          <EmptyMessage>AI Review 선택 기능은 추후 구현 예정입니다.</EmptyMessage>
        </SectionContainer>
      )}

      {data.modes?.reevaluateMode && (
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>평가 결과 선택</SectionTitle>
          </SectionHeader>
          <EmptyMessage>평가 결과 선택 기능은 추후 구현 예정입니다.</EmptyMessage>
        </SectionContainer>
      )}

      <ButtonGroup>
        <Button size="Medium" type="Text" variant="Ghost" color="Secondary" onClick={onBack}>
          이전
        </Button>
        <Button
          size="Medium"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={handleNext}
          disabled={!validate()}
        >
          다음
        </Button>
      </ButtonGroup>
    </Container>
  );
};

// 환자 정보 표시 컴포넌트
const PatientInfo: React.FC<{ patient?: any; patientId: number }> = ({ patient, patientId }) => {
  if (!patient) return <Text>환자 정보 없음</Text>;

  const birthYear = patient.birthDate ? new Date(patient.birthDate).getFullYear() : "정보없음";
  const age = birthYear !== "정보없음" ? new Date().getFullYear() - birthYear + 1 : "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <PatientId>ID: {patientId}</PatientId>
      <PatientDetail>
        {birthYear}년생 {age && `(${age}세)`} / {patient.gender}
      </PatientDetail>
      {patient.memo && (
        <PatientDetail style={{ marginTop: "4px", fontSize: "11px" }}>{patient.memo}</PatientDetail>
      )}
    </div>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const StepDescription = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.5;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const EmptyMessage = styled(Text)`
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  padding: 40px;
`;

const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
`;

const PatientCard = styled.div<{ $selected: boolean }>`
  padding: 16px;
  border: 2px solid
    ${({ $selected }) =>
      $selected ? COLOR_SYSTEM.get("Blue")?.[500] : COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  background-color: ${({ $selected }) => ($selected ? COLOR_SYSTEM.get("Blue")?.[50] : "white")};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    border-color: ${({ $selected }) =>
      $selected ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[300]};
  }
`;

const PatientId = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const PatientDetail = styled(Text)`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const CheckBox = styled.div<{ $selected: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid
    ${({ $selected }) =>
      $selected ? COLOR_SYSTEM.get("Blue")?.[500] : COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 4px;
  background-color: ${({ $selected }) => ($selected ? COLOR_SYSTEM.get("Blue")?.[500] : "white")};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const SelectedCount = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Blue")?.[600]};
  font-weight: 600;
  text-align: right;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

export default ExperimentStep5;
